import React,{ FC }             from 'react';
import DatePicker               from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";

import { Link,useParams }       from "react-router-dom";
import * as UserType            from 'shared/types/User';
import * as VehicleType         from 'shared/types/Vehicle';
import * as OrderType           from 'shared/types/Order';
import * as ManifestType        from 'shared/types/Manifest';
import * as DurationType        from 'shared/types/Duration';
import rehash                   from 'shared/utils/rehash';
import consts                   from 'shared/consts';
import dayjs                    from 'shared/utils/day-timezone';

import * as images              from 'images/';
import * as MenuItem            from 'components/MenuItem';
import * as googleMapsLinks     from 'utils/googleMapsLinks';

import tectransit               from 'utils/TecTransit';
import getApiPromise            from 'utils/getApiPromise';
import deletedOrderStyle        from 'utils/deletedOrderStyle';
import hashById                 from 'shared/utils/hashById';

interface DriverManifest extends ManifestType.VehicleManifest.Hydrated<OrderType.Order<string>> {
    driverSteps : ManifestType.Step.Hydrated<OrderType.Order<string>>[];
}

interface ScheduledOrderProps {
    ndx             : number;
    order           : OrderType.Order<string>;
}
const ScheduledOrder : FC<ScheduledOrderProps> = props => {
    if( !props.order.user?._id ) {
        return (<div className="manifest-inner">
            <div className="manifest-count">{props.ndx+1}</div>
            <div className="manifest-order-item">
                <span style={deletedOrderStyle} title={props.order._id}>deleted order</span>
            </div>
        </div>);
    }
    return (
        <div className="manifest-inner">
            <div className="manifest-count">{props.ndx+1}</div>
            <div className="manifest-order-item">
                <div className="manifest-item-wrap">
                    <div className="manifest-user">
                            <Link to={`/Driver/Passenger/${props.order.user_id}`}>
                                <img width={50} alt={props.order.user?.name} src={props.order.user?.picture}/>
                                {props.order.user?.name}
                            </Link>
                    </div>
                    <div className="manifest-user-content">
                        <div className="manifest-user-item">
                            <div className="manifest-map-title">
                                <images.Pickup/>
                                Pickup:
                            </div>
                            <div className="manifest-map-info">
                                {googleMapsLinks.getPlace(props.order.pickup.location,props.order.pickup.address,googleMapsLinks.onlyFirstLineAddressFormatter)}
                                {(props.order.type==='pickup_ordered_at') && (<> at <b>{dayjs(props.order.ordered_at).tz(tectransit.agency.time_zone).format("HH:mm")}</b></>)}
                            </div>
                        </div>
                        <div className="manifest-user-item">
                            <div className="manifest-map-title">
                                <images.Dropoff/>
                                Dropoff:
                            </div>
                            <div className="manifest-map-info">
                                {googleMapsLinks.getPlace(props.order.dropoff.location,props.order.dropoff.address,googleMapsLinks.onlyFirstLineAddressFormatter)}
                                {(props.order.type==='dropoff_ordered_at') && (<>  at <b>{dayjs(props.order.ordered_at).tz(tectransit.agency.time_zone).format("HH:mm")}</b></>)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

interface RouteStepProps {
    ndx         : number;
    step        : ManifestType.Step.Hydrated<OrderType.Order<string>>;
    nextStep    : (ManifestType.Step.Hydrated<OrderType.Order<string>>|undefined);
}
const RouteStep : FC<RouteStepProps> = props => {
    const step = props.step;
    const getOrderSpan = ( so:OrderType.Order<string>, ndx:number ) => {
        const contents = !so.user?._id ? <span style={deletedOrderStyle} title={so._id}>deleted order</span> : (<Link to={`/Dispatcher/Passenger/${so?.user_id}`}>
            {so?.user?.name}
        </Link>);
        return (<span key={so._id}>
            {(ndx===0)?(' of '):(', ')}
            { contents }
        </span>);
    }
    const [
        stepName,
        stepLatLng,
        stepAddress
    ] = (step.name==='fixedroute') ? [
        step.frTrip!.name,
        step.frTrip!.stops[0],
        step.frTrip!.stops[0].name
    ] : [
        step.name,
        step.latlng,
        step.address
    ];
    return (
        <div className="manifest-inner">
            <div className="manifest-count">{props.ndx+1}</div>
            <div className="manifest-inwrap">
                {step.latlng && (
                    <div className="manifest-user-item">
                        <div className="manifest-map-title">Location:</div>
                        <div className="manifest-map-info">{googleMapsLinks.getPlace(stepLatLng,stepAddress,googleMapsLinks.onlyFirstLineAddressFormatter)}</div>
                    </div>
                )}
                <div className="manifest-inwrap">
                    <div className="manifest-user-item">
                        <div className="manifest-map-title">{stepName}{step.etc?` (${step.etc})`:''}</div>
                        <div className="manifest-map-info">
                            at <b>{dayjs(step.seconds!*1000).tz(tectransit.agency.time_zone).format("HH:mm")}</b> for {DurationType.secondsToHMS(step.duration!)}
                            {((step.pickups?.length||0)>0) && (<div>
                                <strong>pickup</strong>
                                {step.pickups?.map(getOrderSpan)}
                            </div>)}
                            {((step.dropoffs?.length||0)>0) && (<div>
                                <strong>dropoff</strong>
                                {step.dropoffs?.map(getOrderSpan)}
                            </div>)}
                            {((step.seconds!+step.duration!)<(props.nextStep?.seconds||0)) && (
                            <>
                                &nbsp;and {DurationType.secondsToHMS(props.nextStep!.seconds!-step.seconds!-step.duration!,60)} wait
                            </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

interface ManifestProps {
    type            : ('ondemand'|'fixedroute')
    driverManifest  : DriverManifest;
    vehicle         : VehicleType.Vehicle;
}
const JustManifest : FC<ManifestProps> = props => {
    const depot             = tectransit.agency.depots.find(d=>(d.name===props.vehicle.depot_name));
    const problems          = ManifestType.VehicleManifest.getProblems(props.driverManifest);
    const weekday           = dayjs(props.driverManifest.dayStartAt).tz(tectransit.agency.time_zone).format('dddd').toLowerCase();
    const scheduleRanges    = (tectransit.agency.schedule_by_driver_id[tectransit.user._id]?.[weekday]||[]).filter( r => {
        return r[2]===props.vehicle._id;
    });
    if( (props.driverManifest.steps||[]).length<1 )
        problems.push(`got empty route`);
    if( scheduleRanges.length<1 )
        problems.push(`found no schedule ranges for vehicle #${props.vehicle._id}`);
    return (
        <div>
            <div className="manifest-alert">
                At <span>{dayjs(props.driverManifest.requested_at).tz(tectransit.agency.time_zone).format(tectransit.timeFormat)}</span>{" "}
                dispatcher assigned you the following route for the day of&nbsp;
                <strong>{dayjs(props.driverManifest.dayStartAt).tz(tectransit.agency.time_zone).format("MM/DD/YYYY")}</strong>&nbsp;
                on vehicle&nbsp;<strong>{props.vehicle._id}</strong>.
            </div>
            <div className="section-card top-1">
                <div className="manifest-item">
                    <div className="manifest-title">Depot:</div>
                    <div className="manifest-text">{depot ? googleMapsLinks.getPlace(depot,depot.name) : 'n/a'}</div>
                </div>
                <div className="manifest-item">
                    <div className="manifest-title">Vehicle Fleet #:</div>
                    <div className="manifest-text">{props.driverManifest.vehicle_id}</div>
                </div>
                {(props.type==='ondemand') && (
                    <div className="section-card__header">
                        <div className="section-card__title"><br/>Orders</div>
                    </div>
                )}
                <div className="manifest-orders top-1">
                    {Object
                        .values(ManifestType.Step.getOrdersById(props.driverManifest.driverSteps))
                        .map((order,ndx)=>(<ScheduledOrder key={ndx} ndx={ndx} order={order}/>))}
                </div>
                { (problems.length>0) ? (
                    <div className="text-center"><br/><b>
                        {problems.join(',')}</b>
                    </div>
                ) : (
                  <div>
                      <div className="section-card__header">
                          <div className="section-card__title">Route</div>
                      </div>
                      <div className="manifest-orders top-1">
                            {props.driverManifest.driverSteps.map((step,ndx,steps) => {
                                return (
                                    <RouteStep
                                        key     = {ndx}
                                        ndx     = {ndx}
                                        step    = {step}
                                        nextStep= {steps[ndx+1]}
                                    />
                                );
                            })}
                            <div className="manifest-total">
                                <div className="manifest-total-title">Total Distance</div>
                                <div className="manifest-total-text">{Math.round(props.driverManifest.distance/consts.meters_in_mile*100)/100} miles</div>
                            </div>
                            {
                                Object.entries(props.driverManifest.steps.reduce((acc,step) => {
                                    acc[step.name] = (acc[step.name]||0)+step.duration!;
                                    return acc;
                                },{} as Record<string,number>)).map( ([name,seconds],sndx) => (
                                    <div key={sndx} className="manifest-total">
                                        <div className="manifest-total-title">{name}</div>
                                        <div className="manifest-total-text">{DurationType.secondsToHMS(seconds)}</div>
                                    </div>
                                ))
                            }
                      </div>
                  </div>
                ) }
            </div>
        </div>
    );
};

interface Props {
    type : ('fixedroute'|'ondemand');
}

export const Manifests : FC<Props> = props => {

    const params                    = useParams() as { yyyymmdd?:string };

    const [manifestsError,
        setManifestsError]          = React.useState<string>('');
    const [driverManifests,
        setDriverManifests]         = React.useState<DriverManifest[]|undefined>();
    const [vehiclesError,
        setVehiclesError]           = React.useState<string>('');
    const [vehiclesById,
        setVehicleById]             = React.useState<Record<number,VehicleType.Vehicle>|undefined>();
    const [manifestsMoment,
        setManifestsMoment]         = React.useState<dayjs.Dayjs>(
        dayjs.tz(/^\d{4}-\d{2}-\d{2}$/.test(params.yyyymmdd||'')?params.yyyymmdd:Date.now(),tectransit.agency.time_zone).startOf('day')
    );
    const [chosenDriverManifest,
        setChosenDriverManifest]    = React.useState<DriverManifest|undefined>();

    // TODO:
    // This is broken because we are using _current_ schedule to show manifests for a _past_.
    const weekdayName               = manifestsMoment.format('dddd').toLowerCase();
    const driverSchedule            = tectransit.agency.schedule_by_driver_id[tectransit.user._id]?.[weekdayName]||[];

    const setDehydratedManifests = (dehydratedVms:ManifestType.VehicleManifest.Dehydrated<string>[]) => {
        const usersById = dehydratedVms.reduce((acc,dvm) => {
            return Object.assign(acc,rehash(dvm.usersById,UserType.hydrate));
        },{} as Record<number,UserType.User>);
        const driverVms = dehydratedVms.map( dvm => {
            const scheduleRanges = driverSchedule.filter(r=>(r[2]===dvm.vehicle_id));
            const hydratedVm     = ManifestType.VehicleManifest.hydrate(
                dvm,
                rehash(dvm.ordersById,order=>OrderType.hydrate(order,usersById)),
                dvm.fixedRouteStopsById||{}
            );
            return {
                ...hydratedVm,
                driverSteps : hydratedVm.steps.filter( step => {
                    const stepMins = (step.seconds!-(hydratedVm.dayStartAt/1000))/60;
                    return scheduleRanges.some(r=>((r[0]<=stepMins) && (stepMins<r[1])));
                })
            };
        });
        setDriverManifests(driverVms);
        if( driverVms.length===1 )
            setChosenDriverManifest(driverVms[0]);
    }
    const manifestYYYYMMDD = manifestsMoment.format('YYYY-MM-DD');
    React.useEffect(() => {
        Promise.all([
            getApiPromise<ManifestType.VehicleManifest.Dehydrated<string>[]>(`/api/driver/${props.type}/manifests`,'GET',undefined,{date:manifestYYYYMMDD})
                .then( res => {
                    if( !res || res.err )
                        throw Error(res?.err||`empty response`);
                    setDehydratedManifests(res);
                })
                .catch( err => {
                    setManifestsError(`Cannot load manifests (${err.message})`)
                }),
            getApiPromise<VehicleType.Vehicle[]>('/api/driver/vehicles')
                .then( res => {
                    if( !res || res.err )
                        throw Error(res?.err||`empty response`);
                    setVehicleById(hashById(res));
                })
                .catch( err => {
                    setVehiclesError(`Cannot load vehicles (${err.message})`);
                })
        ]);
    },[manifestYYYYMMDD,props.type]);

    return MenuItem.withMenuItem(`Manifests for ${manifestYYYYMMDD}`, alert => {
        if( !driverManifests )
            return (<div style={{textAlign:'center'}}>Loading manifests...</div>);
        if( manifestsError ) {
            alert.set(`Cannot load manifests (${manifestsError})`);
            return;
        }
        if( !vehiclesById )
            return (<div style={{textAlign:'center'}}>Loading vehicles...</div>);
        if( vehiclesError ) {
            alert.set(`Cannot load vehicles (${vehiclesError})`);
            return;
        }
        return (
            <div className="wrapper">
                <table style={{margin:'auto',minWidth:"80%"}}>
                <tbody>
                    { (driverManifests.length<1) && (
                    <tr>
                        <td colSpan={2}>
                            Dispatcher has not assigned you any routes for the day of <strong>{manifestsMoment.format("YYYY-DD-MM")}</strong>.
                        </td>
                    </tr>
                    ) }
                    { (driverManifests.length>1) && (<>
                    <tr>
                        <td colSpan={2}>
                            During <strong>{manifestsMoment.format("YYYY-MM-DD")}</strong> you will drive <strong>{driverManifests.length}</strong> different&nbsp;
                            vehicles. Please choose the vehicle below.
                        </td>
                    </tr>
                    { driverManifests.map( manifest => {
                        return (<tr key={manifest.vehicle_id}>
                            <td align="center" colSpan={2}>
                                <Link to="#" onClick={(e) => {
                                    setChosenDriverManifest(manifest);
                                }}>
                                    Vehicle #{manifest.vehicle_id} ({manifest.driverSteps.length}/{manifest.steps.length} steps)
                                </Link>
                            </td>
                        </tr>);
                    })}
                    </>) }
                    { chosenDriverManifest && (
                    <tr>
                        <td colSpan={2}>
                            <JustManifest
                                key             = {chosenDriverManifest._id}
                                type            = {props.type}
                                driverManifest  = {chosenDriverManifest}
                                vehicle         = {vehiclesById[chosenDriverManifest.vehicle_id]||{}}
                            />
                        </td>
                    </tr>)}
                    <tr>
                        <td valign="top">
                            See <strong>{tectransit.user.name}</strong> manifests at other days:
                        </td>
                        <td valign="top">
                            <DatePicker
                                selected            = {manifestsMoment.toDate()}
                                preventOpenOnFocus  = {true}
                                onChange            = {(date:(Date|null)) => {
                                    if( !date )
                                        return;
                                    setManifestsMoment(dayjs(date).tz(tectransit.agency.time_zone).startOf('day'));
                                    setChosenDriverManifest(undefined);
                                }}
                            />
                        </td>
                    </tr>
                </tbody>
                </table>
            </div>
        );
    });
}

export default Manifests;